/**
 * Printable "Active Operating Week" sheet — pure HTML-string builder
 * (no DOM access, fully unit-testable).
 *
 * Layout philosophy: a FLOWING document, not a fixed 2×2 grid — a colored
 * quadrant overview up top, then full zone breakdowns stacked in canonical
 * order (Red → Green → Yellow → Grey). Flowing content reflows onto any
 * paper size (Letter, A4, …) without clipping, and long weeks simply run
 * onto page two with zone sections kept intact where possible.
 */

import type { Task, ZoneId } from '../types';
import { ZONES } from '../types';

/** Print palette per zone (kept explicit — Tailwind classes don't exist here). */
const PRINT_COLORS: Record<ZoneId, { accent: string; bg: string; text: string }> = {
  zone1: { accent: '#dc2626', bg: '#fef2f2', text: '#991b1b' },
  zone2: { accent: '#059669', bg: '#ecfdf5', text: '#065f46' },
  zone3: { accent: '#d97706', bg: '#fffbeb', text: '#92400e' },
  zone4: { accent: '#475569', bg: '#f8fafc', text: '#334155' },
};

export type PrintFilter = ZoneId | 'all';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const box = (checked: boolean) =>
  `<span class="box${checked ? ' done' : ''}">${checked ? '✓' : ''}</span>`;

function taskRow(task: Task): string {
  const meta: string[] = [];
  if (task.deadlineHint) meta.push(`⏰ ${escapeHtml(task.deadlineHint)}`);
  if (task.rolloverCount > 0) meta.push(`carried ${task.rolloverCount}×`);
  const steps = task.steps
    .map(
      (s) =>
        `<div class="step">${box(s.done)}<span class="${s.done ? 'struck' : ''}">${escapeHtml(
          s.text,
        )} <em>(${s.minutes} min)</em></span></div>`,
    )
    .join('');
  return `<div class="task">
    <div class="task-line">${box(task.done)}<span class="${task.done ? 'struck' : ''}">${escapeHtml(
      task.text,
    )}</span>${meta.length ? `<span class="meta">${meta.join(' · ')}</span>` : ''}</div>
    ${steps}
  </div>`;
}

/**
 * Build the complete printable HTML document.
 * @param filter 'all' for the full week, or a single zone id.
 */
export function buildPrintHtml(
  tasks: Task[],
  weekLabelText: string,
  filter: PrintFilter = 'all',
  printedOn = '',
): string {
  const zones = ZONES.filter((z) => filter === 'all' || z.id === filter);
  const byZone = (id: ZoneId) => tasks.filter((t) => t.zone === id);

  const overview =
    filter === 'all'
      ? `<div class="quadrants">
          ${ZONES.map((z) => {
            const c = PRINT_COLORS[z.id];
            const open = byZone(z.id).filter((t) => !t.done).length;
            return `<div class="quad" style="background:${c.bg};border-color:${c.accent}">
              <div class="quad-title" style="color:${c.text}">${z.emoji} ${escapeHtml(z.title)}</div>
              <div class="quad-sub">${escapeHtml(z.subtitle)}</div>
              <div class="quad-count" style="color:${c.text}">${open} open</div>
            </div>`;
          }).join('')}
        </div>`
      : '';

  const sections = zones
    .map((z) => {
      const c = PRINT_COLORS[z.id];
      const zoneTasks = byZone(z.id);
      const open = zoneTasks.filter((t) => !t.done);
      const done = zoneTasks.filter((t) => t.done);
      return `<section class="zone">
        <header class="zone-head" style="background:${c.accent}">
          <span>${z.emoji} ${escapeHtml(z.title)}</span>
          <span class="zone-sub">${escapeHtml(z.subtitle)}</span>
        </header>
        <div class="zone-body" style="background:${c.bg}">
          ${
            zoneTasks.length === 0
              ? '<div class="empty">Nothing in this zone — enjoy the space.</div>'
              : [...open, ...done].map(taskRow).join('')
          }
        </div>
      </section>`;
    })
    .join('');

  const title =
    filter === 'all'
      ? `Active Operating Week${weekLabelText ? ` — ${weekLabelText}` : ''}`
      : `${ZONES.find((z) => z.id === filter)?.title ?? ''}${weekLabelText ? ` — ${weekLabelText}` : ''}`;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  /* Inter first for brand consistency; the system stack behind it is the
     safety net. This sheet is rendered in a separate print window, so the
     font may or may not resolve there — if it does not, the output is
     byte-for-byte what it was before this line changed. Non-regressive
     either way, which is why it is safe to ship without a print test. */
  body { font-family: Inter, -apple-system, 'Segoe UI', Arial, sans-serif; color: #0f172a;
         margin: 0; padding: 24px; font-size: 12px; line-height: 1.45; }
  .sheet-head { display: flex; justify-content: space-between; align-items: baseline;
                border-bottom: 3px solid #4f46e5; padding-bottom: 8px; margin-bottom: 14px; }
  .brand { font-size: 17px; font-weight: 800; color: #4f46e5; }
  .sheet-title { font-size: 13px; font-weight: 700; }
  .printed { font-size: 10px; color: #64748b; }
  .quadrants { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
  .quad { border: 2px solid; border-radius: 8px; padding: 8px 10px; }
  .quad-title { font-weight: 800; font-size: 12px; }
  .quad-sub { font-size: 10px; color: #475569; margin-top: 2px; }
  .quad-count { font-size: 10px; font-weight: 700; margin-top: 4px; }
  .zone { margin-bottom: 14px; break-inside: avoid-page; }
  .zone-head { color: #fff; border-radius: 6px 6px 0 0; padding: 6px 10px; font-weight: 800;
               font-size: 12px; display: flex; justify-content: space-between; align-items: baseline; }
  .zone-sub { font-weight: 400; font-size: 10px; opacity: 0.9; }
  .zone-body { border-radius: 0 0 6px 6px; padding: 8px 10px; }
  .task { padding: 4px 0; break-inside: avoid; }
  .task-line { display: flex; align-items: baseline; gap: 7px; font-weight: 600; }
  .step { display: flex; align-items: baseline; gap: 7px; margin: 2px 0 0 26px; font-weight: 400; color: #334155; }
  .box { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #475569;
         border-radius: 3px; flex-shrink: 0; text-align: center; line-height: 11px;
         font-size: 10px; color: #059669; transform: translateY(2px); }
  .box.done { border-color: #059669; }
  .struck { text-decoration: line-through; color: #94a3b8; }
  .meta { font-size: 10px; color: #64748b; font-weight: 400; margin-left: 4px; }
  .empty { font-style: italic; color: #64748b; font-size: 11px; padding: 2px 0; }
  em { font-style: normal; color: #64748b; font-size: 10px; }
  .sheet-foot { margin-top: 16px; text-align: center; font-size: 9px; color: #94a3b8; }
  @page { margin: 14mm; }
</style>
</head>
<body>
  <div class="sheet-head">
    <span class="brand">ZoneIn AI</span>
    <span class="sheet-title">${escapeHtml(title)}</span>
    ${printedOn ? `<span class="printed">Printed ${escapeHtml(printedOn)}</span>` : ''}
  </div>
  ${overview}
  ${sections}
  <div class="sheet-foot">zonein-82a22.web.app · screen to plan, paper to execute</div>
</body>
</html>`;
}
